/**
 * CE-39 R4 — Reports page scope badges.
 *
 * One chip per attribute value the active viewer is scoped by. The chip's
 * background/foreground come from the shared ATTRIBUTE_TYPE_CHIP palette so
 * the styling matches AttributesMultiSelect's dropdown rows — Course is
 * blue, Faculty is purple. The chip label shows only the value (e.g.
 * "Boston"); the hover tooltip surfaces the full context — attribute
 * type + title + value (e.g. "Course Attributes • Campus: Boston").
 *
 * When the active scope contains more than `VISIBLE_LIMIT` values, the row
 * shows only the first N chips followed by a "+M more" toggle chip; clicking
 * the toggle expands the remaining chips inline (and swaps to "Show less").
 *
 * A trailing info icon explains what the badges mean: the report numbers
 * below are filtered by the listed scopes.
 *
 * Returns null when no scope is set — no wrapper Box, no spacer.
 */
import React, { useState } from 'react'
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CustomTooltip from '../../../../components/CustomTooltip'
import { grey } from '../../../../colors'
import { ATTRIBUTE_TYPES, ATTRIBUTE_TYPE_CHIP } from '../../../../consts'
import {
  type AppliedAttributeScope,
  type AttributeScopeEntry
} from '../../helpers/deriveAppliedAttributeScope'

interface ScopeBadgesProps {
  appliedAttributeScope: AppliedAttributeScope
}

interface ScopedAttribute {
  attributeType: typeof ATTRIBUTE_TYPES[keyof typeof ATTRIBUTE_TYPES]
  entry: AttributeScopeEntry
}

// How many chips to show before collapsing the rest behind a "+N more" toggle.
// Most users have well under this; the toggle only appears for scoped Viewers
// with many attribute values on their role (e.g. a 50-campus scope).
const VISIBLE_LIMIT = 5

const flatten = ({ courseAttributes, facultyAttributes }: AppliedAttributeScope): ScopedAttribute[] => [
  ...courseAttributes.map(entry => ({ attributeType: ATTRIBUTE_TYPES.COURSE_ATTRIBUTES, entry })),
  ...facultyAttributes.map(entry => ({ attributeType: ATTRIBUTE_TYPES.FACULTY_COURSE_ATTRIBUTES, entry }))
]

const HelpTooltipContent: React.FC = () => (
  <Box sx={{ maxWidth: 320 }}>
    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: 'white', mb: 0.75 }}>
      Filtered report — scoped to the attributes. Affects:
    </Typography>
    <Box component="ul" sx={{ pl: 2, m: 0, color: 'white' }}>
      <Typography component="li" variant="body2" sx={{ fontSize: 12, mb: 0.5 }}>
        Response counts and stats (average, median, completion rate).
      </Typography>
      <Typography component="li" variant="body2" sx={{ fontSize: 12, mb: 0.5 }}>
        Completion Rates table — only sections and instructors in scope.
      </Typography>
      <Typography component="li" variant="body2" sx={{ fontSize: 12 }}>
        Repeat per Instructor sections — only matching instructors.
      </Typography>
    </Box>
  </Box>
)

const ScopeChip: React.FC<{ item: ScopedAttribute }> = ({ item }) => {
  const palette = ATTRIBUTE_TYPE_CHIP[item.attributeType]
  const tooltipLabel = `${item.attributeType} • ${item.entry.title}: ${item.entry.value}`
  return (
    <Tooltip title={tooltipLabel} arrow>
      <Chip
        size="small"
        label={item.entry.value}
        sx={{ bgcolor: palette.bg, color: palette.fg, fontWeight: 500 }}
      />
    </Tooltip>
  )
}

const ScopeBadges: React.FC<ScopeBadgesProps> = ({ appliedAttributeScope }) => {
  const [expanded, setExpanded] = useState(false)
  const items = flatten(appliedAttributeScope)
  if (items.length === 0) return null

  const isOverLimit = items.length > VISIBLE_LIMIT
  const visibleItems = expanded || !isOverLimit ? items : items.slice(0, VISIBLE_LIMIT)
  const hiddenCount = items.length - VISIBLE_LIMIT

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      {visibleItems.map((item, idx) => (
        <ScopeChip key={`${item.attributeType}-${item.entry.title}-${item.entry.value}-${idx}`} item={item} />
      ))}
      {isOverLimit && (
        <Chip
          size="small"
          label={expanded ? 'Show less' : `+${hiddenCount} more`}
          onClick={() => { setExpanded(prev => !prev) }}
          aria-expanded={expanded}
          sx={{
            bgcolor: grey[200],
            color: grey[800],
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': { bgcolor: grey[300] }
          }}
        />
      )}
      <CustomTooltip title={<HelpTooltipContent />} placement="top">
        <InfoOutlinedIcon
          aria-label="About these filters"
          sx={{ color: grey[600], fontSize: 18, cursor: 'pointer' }}
        />
      </CustomTooltip>
    </Box>
  )
}

export default ScopeBadges
